import { useState } from 'react';
import { User, Settings, Bell, Shield, HelpCircle, LogOut, Edit, Camera, Globe, Moon, Sun } from 'lucide-react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Card,
  CardBody,
  Badge,
  Avatar,
  Switch,
  Select,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Divider,
  Icon,
  IconButton,
  useColorModeValue
} from '@chakra-ui/react';

export function ProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [autoSave, setAutoSave] = useState(true);
  const [language, setLanguage] = useState('ru');

  const bgGradient = useColorModeValue(
    'linear(to-b, blue.50, white)', 
    'linear(to-b, gray.900, gray.800)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [profile, setProfile] = useState({
    name: 'Алексей Петров',
    email: 'alexey.petrov@example.com',
    company: 'ООО "Технологии будущего"',
    position: 'Менеджер проектов',
    phone: '+7 (999) 123-45-67'
  });

  const handleSaveProfile = () => {
    setIsEditing(false);
    // Здесь бы была логика сохранения в API
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Box maxW="md" mx="auto" p={4} pb={20}>
        {/* Header */}
        <VStack spacing={4} textAlign="center" mb={8} pt={8}>
          <Box position="relative">
            <Avatar
              size="xl"
              bg="linear-gradient(135deg, #3b82f6, #8b5cf6)"
              icon={<Icon as={User} w={10} h={10} color="white" />}
            />
            <IconButton
              size="sm"
              position="absolute"
              bottom={-1}
              right={-1}
              borderRadius="full"
              colorScheme="blue"
              aria-label="Change photo"
              icon={<Icon as={Camera} w={4} h={4} />}
            />
          </Box>
          <VStack spacing={2}>
            <Heading size="lg" color={textColor}>{profile.name}</Heading>
            <Text color={mutedColor} fontSize="sm">{profile.email}</Text>
            <Badge colorScheme="purple" mt={2}>
              Premium аккаунт
            </Badge>
          </VStack>
        </VStack>

        {/* Profile Information */}
        <Card bg={cardBg} mb={6}>
          <CardBody p={4}>
            <HStack justify="space-between" mb={4}>
              <Heading size="md" fontWeight="medium" color={textColor}>
                Информация профиля
              </Heading>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                leftIcon={<Icon as={Edit} w={4} h={4} />}
              >
                {isEditing ? 'Сохранить' : 'Изменить'}
              </Button>
            </HStack>

            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={mutedColor}>Имя</FormLabel>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                  isDisabled={!isEditing}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={mutedColor}>Email</FormLabel>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  isDisabled={!isEditing}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={mutedColor}>Компания</FormLabel>
                <Input
                  value={profile.company}
                  onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
                  isDisabled={!isEditing}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={mutedColor}>Должность</FormLabel>
                <Input
                  value={profile.position}
                  onChange={(e) => setProfile(prev => ({ ...prev, position: e.target.value }))}
                  isDisabled={!isEditing}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={mutedColor}>Телефон</FormLabel>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                  isDisabled={!isEditing}
                />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Settings */}
        <Card bg={cardBg} mb={6}>
          <CardBody p={4}>
            <HStack spacing={2} mb={4}>
              <Icon as={Settings} w={5} h={5} />
              <Heading size="md" fontWeight="medium" color={textColor}>
                Настройки
              </Heading>
            </HStack>

            <VStack spacing={4}>
              {/* Language */}
              <HStack justify="space-between" w="full">
                <HStack spacing={3}>
                  <Icon as={Globe} w={5} h={5} color={mutedColor} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" color={textColor}>Язык</Text>
                    <Text fontSize="sm" color={mutedColor}>
                      Выберите язык интерфейса
                    </Text>
                  </VStack>
                </HStack>
                <Select value={language} onChange={(e) => setLanguage(e.target.value)} w={24}>
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </Select>
              </HStack>

              <Divider />

              {/* Dark Mode */}
              <HStack justify="space-between" w="full">
                <HStack spacing={3}>
                  <Icon as={darkMode ? Moon : Sun} w={5} h={5} color={mutedColor} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" color={textColor}>Тёмная тема</Text>
                    <Text fontSize="sm" color={mutedColor}>
                      Переключить на тёмную тему
                    </Text>
                  </VStack>
                </HStack>
                <Switch
                  isChecked={darkMode}
                  onChange={(e) => setDarkMode(e.target.checked)}
                />
              </HStack>

              <Divider />

              {/* Notifications */}
              <HStack justify="space-between" w="full">
                <HStack spacing={3}>
                  <Icon as={Bell} w={5} h={5} color={mutedColor} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" color={textColor}>Уведомления</Text>
                    <Text fontSize="sm" color={mutedColor}>
                      Получать уведомления от ИИ
                    </Text>
                  </VStack>
                </HStack>
                <Switch
                  isChecked={notifications}
                  onChange={(e) => setNotifications(e.target.checked)}
                />
              </HStack>

              <Divider />

              {/* Auto Save */}
              <HStack justify="space-between" w="full">
                <HStack spacing={3}>
                  <Icon as={Shield} w={5} h={5} color={mutedColor} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" color={textColor}>Автосохранение</Text>
                    <Text fontSize="sm" color={mutedColor}>
                      Автоматически сохранять чаты
                    </Text>
                  </VStack>
                </HStack>
                <Switch
                  isChecked={autoSave}
                  onChange={(e) => setAutoSave(e.target.checked)}
                />
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Support and Help */}
        <Card bg={cardBg} mb={6}>
          <CardBody p={4}>
            <Heading size="md" fontWeight="medium" color={textColor} mb={4}>
              Поддержка
            </Heading>
            
            <VStack spacing={3}>
              <Button variant="ghost" justifyContent="flex-start" w="full">
                <Icon as={HelpCircle} w={5} h={5} mr={3} />
                Помощь и FAQ
              </Button>
              
              <Button variant="ghost" justifyContent="flex-start" w="full">
                <Icon as={Shield} w={5} h={5} mr={3} />
                Политика конфиденциальности
              </Button>
              
              <Divider />
              
              <Button 
                variant="ghost" 
                justifyContent="flex-start" 
                w="full"
                color="red.500"
                _hover={{ color: "red.600", bg: "red.50" }}
              >
                <Icon as={LogOut} w={5} h={5} mr={3} />
                Выйти из аккаунта
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {/* Usage Statistics */}
        <Card bg={cardBg}>
          <CardBody p={4}>
            <Heading size="md" fontWeight="medium" color={textColor} mb={4}>
              Статистика использования
            </Heading>
            
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem textAlign="center">
                <Text fontSize="2xl" fontWeight="semibold" color="blue.500">142</Text>
                <Text fontSize="sm" color={mutedColor}>Всего чатов</Text>
              </GridItem>
              <GridItem textAlign="center">
                <Text fontSize="2xl" fontWeight="semibold" color="blue.500">28</Text>
                <Text fontSize="sm" color={mutedColor}>Дней активности</Text>
              </GridItem>
              <GridItem textAlign="center">
                <Text fontSize="2xl" fontWeight="semibold" color="blue.500">1,247</Text>
                <Text fontSize="sm" color={mutedColor}>Сообщений</Text>
              </GridItem>
              <GridItem textAlign="center">
                <Text fontSize="2xl" fontWeight="semibold" color="blue.500">12</Text>
                <Text fontSize="sm" color={mutedColor}>Интеграций</Text>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}